import React, { useState, useEffect } from 'react';
import './RegisterDetailsModal.css';
import apiService from '../../services/api';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';
import dateUtils from '../../utils/dateUtils';

const RegisterDetailsModal = ({ isOpen, onClose, userDetails, addToast }) => {
  const { formatCurrency, currencySymbol } = useBusinessSettings();
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registerData, setRegisterData] = useState(null);
  const [openRegisterId, setOpenRegisterId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const bizData = await apiService.getBusinessDetails();
        setBusinessInfo(bizData.data || bizData);

        let openRegister = await apiService.getOpenCashRegister();
        let details = null;
        if (openRegister && openRegister.id) {
          setOpenRegisterId(openRegister.id);
          details = await apiService.getCashRegisterDetails(openRegister.id);
        }
        if (!details || (!details.register_details && !details.data?.register_details)) {
          const fallbackDetails = await apiService.getCashRegisterDetails();
          if (fallbackDetails) details = fallbackDetails;
        }

        if (details) {
          setRegisterData(details);
          const root = details.data || details;
          const regData = root.register_details || {};
          const openTimeRaw = regData.open_time || openRegister?.created_at || '';
          const closeTimeRaw = regData.closed_at || details.close_time || new Date();

          const formattedOpen = openTimeRaw ? dateUtils.formatDateTime(openTimeRaw) : dateUtils.formatDateTime(new Date());
          const formattedClose = closeTimeRaw ? dateUtils.formatDateTime(closeTimeRaw) : dateUtils.formatDateTime(new Date());

          setCurrentDateTime(`${formattedOpen} - ${formattedClose}`);
        }
      } catch (error) {
        /* console.error('Error loading register details:', error); */
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen]);

  if (!isOpen) return null;

  const rootData = registerData?.data || registerData || {};
  const reg = rootData.register_details || {};
  const trans = rootData.transaction_details || {};
  const products = Array.isArray(rootData.product_details) ? rootData.product_details : Object.values(rootData.product_details || {});
  const brands = Array.isArray(rootData.product_details_by_brand) ? rootData.product_details_by_brand : Object.values(rootData.product_details_by_brand || {});
  const denominations = reg.denominations || null;

  const cashInHand = parseFloat(reg.cash_in_hand || 0);
  const totalCash = parseFloat(reg.total_cash || 0);
  const totalCashExpense = parseFloat(reg.total_cash_expense || 0);
  const totalCheque = parseFloat(reg.total_cheque || 0);
  const totalChequeExpense = parseFloat(reg.total_cheque_expense || 0);
  const totalCard = parseFloat(reg.total_card || 0);
  const totalCardExpense = parseFloat(reg.total_card_expense || 0);
  const totalBankTransfer = parseFloat(reg.total_bank_transfer || 0);
  const totalBankTransferExpense = parseFloat(reg.total_bank_transfer_expense || 0);
  const totalAdvance = parseFloat(reg.total_advance || 0);
  const totalAdvanceExpense = parseFloat(reg.total_advance_expense || 0);
  const totalOther = parseFloat(reg.total_other || 0);
  const totalOtherExpense = parseFloat(reg.total_other_expense || 0);

  const totalSale = parseFloat(reg.total_sale || trans.total_sales || 0);
  const totalRefund = parseFloat(reg.total_refund || 0);
  const totalCashRefund = parseFloat(reg.total_cash_refund || 0);
  const totalChequeRefund = parseFloat(reg.total_cheque_refund || 0);
  const totalCardRefund = parseFloat(reg.total_card_refund || 0);
  const totalBankTransferRefund = parseFloat(reg.total_bank_transfer_refund || 0);
  const totalOtherRefund = parseFloat(reg.total_other_refund || 0);

  const totalPayment = cashInHand + totalCash - totalCashRefund;
  const overallSales = parseFloat(trans.total_sales || reg.total_sale || 0);
  const creditSales = Math.max(0, overallSales - totalSale);
  const totalExpense = parseFloat(reg.total_expense || 0);

  // Totals for products & brands
  const totalProdQty = products.reduce((acc, i) => acc + (parseFloat(i.total_quantity !== undefined ? i.total_quantity : (i.qty || i.quantity || 0))), 0);
  const totalProdAmount = products.reduce((acc, i) => acc + (parseFloat(i.total || i.total_amount || 0)), 0);

  const totalBrandQty = brands.reduce((acc, i) => acc + (parseFloat(i.total_quantity !== undefined ? i.total_quantity : (i.qty || i.quantity || 0))), 0);
  const totalBrandAmount = brands.reduce((acc, i) => acc + (parseFloat(i.total || i.total_amount || 0)), 0);

  const totalTax = parseFloat(trans.total_tax || 0);
  const totalDiscount = parseFloat(trans.total_discount || 0);
  const totalShipping = parseFloat(trans.total_shipping_charges || 0);

  // Web Blade formula: sum(items.total_amount) + (total_tax - total_discount) + total_shipping_charges
  const productGrandTotal = totalProdAmount + (totalTax - totalDiscount) + totalShipping;
  const brandGrandTotal = totalBrandAmount + (totalTax - totalDiscount) + totalShipping;

  // Denominations calculation
  let denomTotal = 0;
  const denomRows = [];
  if (denominations && typeof denominations === 'object') {
    Object.keys(denominations).forEach(key => {
      const count = parseInt(denominations[key] || 0, 10);
      const sub = parseFloat(key) * count;
      denomTotal += sub;
      denomRows.push({ denom: key, count, sub });
    });
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="rd-modal-overlay" onClick={onClose}>
      <div className="rd-modal-container" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rd-modal-header">
          <h3 className="rd-title">
            Register Details <span className="rd-date-range">( {currentDateTime} )</span>
          </h3>
          <button className="rd-close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="rd-modal-body">
          {loading ? (
            <div className="rd-loading">
              <div className="rd-spinner" />
              <p>Loading register details…</p>
            </div>
          ) : (
            <>
              {/* Payment Methods Summary */}
              <div className="rd-table-responsive">
                <table className="rd-table rd-table-bordered">
                  <thead>
                    <tr>
                      <th>Payment Method</th>
                      <th>Sell</th>
                      <th>Expense</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Cash in hand:</td>
                      <td>{formatCurrency(cashInHand)}</td>
                      <td>--</td>
                    </tr>
                    <tr>
                      <td>Cash Payment:</td>
                      <td>{formatCurrency(totalCash)}</td>
                      <td>{formatCurrency(totalCashExpense)}</td>
                    </tr>
                    <tr>
                      <td>Cheque Payment:</td>
                      <td>{formatCurrency(totalCheque)}</td>
                      <td>{formatCurrency(totalChequeExpense)}</td>
                    </tr>
                    <tr>
                      <td>Card Payment:</td>
                      <td>{formatCurrency(totalCard)}</td>
                      <td>{formatCurrency(totalCardExpense)}</td>
                    </tr>
                    <tr>
                      <td>Bank Transfer:</td>
                      <td>{formatCurrency(totalBankTransfer)}</td>
                      <td>{formatCurrency(totalBankTransferExpense)}</td>
                    </tr>
                    <tr>
                      <td>Advance payment:</td>
                      <td>{formatCurrency(totalAdvance)}</td>
                      <td>{formatCurrency(totalAdvanceExpense)}</td>
                    </tr>
                    <tr>
                      <td>Other Payments:</td>
                      <td>{formatCurrency(totalOther)}</td>
                      <td>{formatCurrency(totalOtherExpense)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <hr className="rd-hr" />

              {/* Totals Summary */}
              <div className="rd-table-responsive">
                <table className="rd-table rd-table-bordered">
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: 600 }}>Total Sales:</td>
                      <td>{formatCurrency(totalSale)}</td>
                    </tr>
                    <tr className="rd-tr-danger">
                      <th style={{ textAlign: 'left' }}>Total Refund</th>
                      <td>
                        <strong>{formatCurrency(totalRefund)}</strong>
                        {(totalCashRefund > 0 || totalChequeRefund > 0 || totalCardRefund > 0 || totalBankTransferRefund > 0 || totalOtherRefund > 0) && (
                          <div style={{ fontSize: '0.75rem', marginTop: '4px', fontWeight: 'normal' }}>
                            {totalCashRefund > 0 && <div>Cash: {formatCurrency(totalCashRefund)}</div>}
                            {totalChequeRefund > 0 && <div>Cheque: {formatCurrency(totalChequeRefund)}</div>}
                            {totalCardRefund > 0 && <div>Card: {formatCurrency(totalCardRefund)}</div>}
                            {totalBankTransferRefund > 0 && <div>Bank Transfer: {formatCurrency(totalBankTransferRefund)}</div>}
                            {totalOtherRefund > 0 && <div>Other: {formatCurrency(totalOtherRefund)}</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr className="rd-tr-success">
                      <th style={{ textAlign: 'left' }}>Total Payment</th>
                      <td><strong>{formatCurrency(totalPayment)}</strong></td>
                    </tr>
                    <tr className="rd-tr-success">
                      <th style={{ textAlign: 'left' }}>Credit Sales:</th>
                      <td><strong>{formatCurrency(creditSales)}</strong></td>
                    </tr>
                    <tr className="rd-tr-success">
                      <th style={{ textAlign: 'left' }}>Total Sales:</th>
                      <td><strong>{formatCurrency(overallSales)}</strong></td>
                    </tr>
                    <tr className="rd-tr-danger">
                      <th style={{ textAlign: 'left' }}>Total Expense:</th>
                      <td><strong>{formatCurrency(totalExpense)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Cash Denominations Section if present */}
              {denomRows.length > 0 && (
                <>
                  <hr className="rd-hr" />
                  <div className="rd-section">
                    <h4 className="rd-section-subtitle">Cash Denominations</h4>
                    <div className="rd-table-responsive" style={{ maxWidth: '600px' }}>
                      <table className="rd-table rd-table-slim">
                        <thead>
                          <tr>
                            <th className="text-right">Denomination</th>
                            <th className="text-center">&nbsp;</th>
                            <th className="text-center">Count</th>
                            <th className="text-center">&nbsp;</th>
                            <th className="text-left">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {denomRows.map((r, i) => (
                            <tr key={i}>
                              <td className="text-right">{r.denom}</td>
                              <td className="text-center">X</td>
                              <td className="text-center">{r.count}</td>
                              <td className="text-center">=</td>
                              <td className="text-left">{formatCurrency(r.sub)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colSpan="4" className="text-center">Total</th>
                            <td><strong>{formatCurrency(denomTotal)}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              )}

              <hr className="rd-hr" />

              {/* Details of products sold */}
              <div className="rd-section">
                <h4 className="rd-section-subtitle">Details of products sold</h4>
                <div className="rd-table-responsive">
                  <table className="rd-table rd-table-bordered">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Total amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products && products.length > 0 ? (
                        products.map((item, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}.</td>
                            <td>{item.sku || '—'}</td>
                            <td>{item.product || item.product_name || item.name}</td>
                            <td>{parseFloat(item.total_quantity !== undefined ? item.total_quantity : (item.qty || item.quantity || 0)).toFixed(2)}</td>
                            <td>{formatCurrency(item.total || item.total_amount || 0)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', color: '#94a3b8', padding: '16px' }}>No products sold in this register session yet</td>
                        </tr>
                      )}
                    </tbody>
                    {products && products.length > 0 && (
                      <tfoot>
                        <tr className="rd-tr-success" style={{ fontWeight: 700 }}>
                          <td>#</td>
                          <td colSpan="2"></td>
                          <td>{totalProdQty.toFixed(2)}</td>
                          <td>
                            {totalTax !== 0 && <div>Order Tax: (+) {formatCurrency(totalTax)}</div>}
                            {totalDiscount !== 0 && <div>Discount: (-) {formatCurrency(totalDiscount)}</div>}
                            {totalShipping !== 0 && <div>Total Shipping Charges: (+) {formatCurrency(totalShipping)}</div>}
                            <div>Grand Total: {formatCurrency(productGrandTotal)}</div>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <hr className="rd-hr" />

              {/* Details of products sold (By Brand) */}
              <div className="rd-section">
                <h4 className="rd-section-subtitle">Details of products sold (By Brand)</h4>
                <div className="rd-table-responsive">
                  <table className="rd-table rd-table-bordered">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Brands</th>
                        <th>Quantity</th>
                        <th>Total amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brands && brands.length > 0 ? (
                        brands.map((item, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}.</td>
                            <td>{item.brand || item.brand_name || 'Unbranded'}</td>
                            <td>{parseFloat(item.total_quantity !== undefined ? item.total_quantity : (item.qty || item.quantity || 0)).toFixed(2)}</td>
                            <td>{formatCurrency(item.total || item.total_amount || 0)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8', padding: '16px' }}>No brand details available</td>
                        </tr>
                      )}
                    </tbody>
                    {brands && brands.length > 0 && (
                      <tfoot>
                        <tr className="rd-tr-success" style={{ fontWeight: 700 }}>
                          <td>#</td>
                          <td></td>
                          <td>{totalBrandQty.toFixed(2)}</td>
                          <td>
                            {totalTax !== 0 && <div>Order Tax: (+) {formatCurrency(totalTax)}</div>}
                            {totalDiscount !== 0 && <div>Discount: (-) {formatCurrency(totalDiscount)}</div>}
                            {totalShipping !== 0 && <div>Total Shipping Charges: (+) {formatCurrency(totalShipping)}</div>}
                            <div>Grand Total: {formatCurrency(brandGrandTotal)}</div>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <hr className="rd-hr" />

              {/* User & Location Footer Info */}
              <div className="rd-user-meta">
                <div><strong>User:</strong> {reg.user_name || userDetails?.data?.first_name || 'Admin'}</div>
                <div><strong>Email:</strong> {reg.email || userDetails?.data?.email || 'N/A'}</div>
                <div><strong>Business Location:</strong> {reg.location_name || businessInfo?.name || 'Main Warehouse'}</div>
                {reg.closing_note && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>Closing Note:</strong><br />
                    {reg.closing_note}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Modal Actions */}
        <div className="rd-modal-actions">
          <button className="rd-btn rd-btn--print" onClick={handlePrint}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z"/></svg>
            Print
          </button>
          <button 
            className="rd-btn" 
            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={async () => {
              if (window.confirm('Are you sure you want to close this cash register?')) {
                try {
                  const regId = openRegisterId || (registerData?.data?.register_details?.id || registerData?.register_details?.id);
                  if (regId) {
                    await apiService.closeCashRegister(regId, {
                      closing_note: 'Register closed from SPA POS',
                      closed_at: dateUtils.nowUTC()
                    });
                  }
                  if (typeof addToast === 'function') {
                    addToast('Cash Register closed successfully.', 'success');
                  }
                  onClose();
                  window.location.reload();
                } catch (e) {
                  if (typeof addToast === 'function') {
                    addToast('Error closing register: ' + e.message, 'error');
                  } else {
                    /* console.error(e); */
                  }
                }
              }
            }}
          >
            Close Register
          </button>
          <button className="rd-btn rd-btn--close" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default RegisterDetailsModal;
